import type {
  Organization,
  QuestionnaireResponse,
  QuestionnaireResponseItem,
  Reference,
} from "@medplum/fhirtypes";

import {
  VERIFICATION_SNAPSHOT_LINK_IDS,
  VERIFICATION_SNAPSHOT_META_TAG_SYSTEMS,
} from "@/billing/constants/insurance-verification";
import { Extensions } from "@/fhir/enums/extensions";

export interface InsuranceVerificationSnapshot {
  insuranceCompany: string;
  insuranceId: string;
  insuranceGroupNumber: string;
}

/**
 * Normalizes a string for FHIR `code` (non-empty, trimmed, single internal spaces).
 * Returns undefined when the value cannot be stored as a Coding.code.
 */
export function toFhirMetaTagCode(value: string | undefined | null): string | undefined {
  if (value == null) return undefined;
  const normalized = value.trim().replace(/\s+/g, " ");
  return normalized === "" ? undefined : normalized;
}

/**
 * Builds meta.tag entries for an insurance verification snapshot.
 * Omits fields that would produce an empty or invalid FHIR code.
 */
export function buildInsuranceSnapshotMetaTags(
  snapshot: InsuranceVerificationSnapshot,
): { system: string; code: string }[] {
  const tags: { system: string; code: string }[] = [];

  const insuranceId = toFhirMetaTagCode(snapshot.insuranceId);
  if (insuranceId != null) {
    tags.push({ system: VERIFICATION_SNAPSHOT_META_TAG_SYSTEMS.insuranceId, code: insuranceId });
  }

  const insuranceCompany = toFhirMetaTagCode(snapshot.insuranceCompany);
  if (insuranceCompany != null) {
    tags.push({
      system: VERIFICATION_SNAPSHOT_META_TAG_SYSTEMS.insuranceCompany,
      code: insuranceCompany,
    });
  }

  const insuranceGroupNumber = toFhirMetaTagCode(snapshot.insuranceGroupNumber);
  if (insuranceGroupNumber != null) {
    tags.push({
      system: VERIFICATION_SNAPSHOT_META_TAG_SYSTEMS.insuranceGroupNumber,
      code: insuranceGroupNumber,
    });
  }

  return tags;
}

/**
 * Reads the Organization picked in Step 2 from a verification response.
 * @param response Insurance verification QuestionnaireResponse.
 * @returns The payer reference, or `undefined` for a response saved without one.
 */
export function getInsuranceVerificationPayer(
  response: QuestionnaireResponse,
): Reference<Organization> | undefined {
  const payer = response.extension?.find(
    (extension) => extension.url === Extensions.InsuranceVerificationPayer,
  )?.valueReference;
  return payer?.reference ? (payer as Reference<Organization>) : undefined;
}

/**
 * Reads a questionnaire answer as displayable text.
 * @param item QuestionnaireResponse item that may contain one answer.
 * @returns The first answer's valueString or valueCoding.display, or an empty string.
 */
export function getQuestionnaireAnswerString(item: QuestionnaireResponseItem | undefined): string {
  const value = item?.answer?.[0];
  if (!value) return "";
  return (
    (value as { valueString?: string }).valueString ??
    (value as { valueCoding?: { display?: string } }).valueCoding?.display ??
    ""
  );
}

function parseInsuranceSnapshotFromMetaTags(
  response: QuestionnaireResponse,
): InsuranceVerificationSnapshot | undefined {
  const insuranceId = response.meta?.tag?.find(
    (tag) => tag.system === VERIFICATION_SNAPSHOT_META_TAG_SYSTEMS.insuranceId,
  )?.code;
  const insuranceCompany = response.meta?.tag?.find(
    (tag) => tag.system === VERIFICATION_SNAPSHOT_META_TAG_SYSTEMS.insuranceCompany,
  )?.code;
  const insuranceGroupNumber = response.meta?.tag?.find(
    (tag) => tag.system === VERIFICATION_SNAPSHOT_META_TAG_SYSTEMS.insuranceGroupNumber,
  )?.code;
  if (
    insuranceId === undefined &&
    insuranceCompany === undefined &&
    insuranceGroupNumber === undefined
  ) {
    return undefined;
  }
  return {
    insuranceId: insuranceId ?? "",
    insuranceCompany: insuranceCompany ?? "",
    insuranceGroupNumber: insuranceGroupNumber ?? "",
  };
}

function parseInsuranceSnapshotFromItems(
  response: QuestionnaireResponse,
): InsuranceVerificationSnapshot | undefined {
  if (!response.item?.length) return undefined;
  const byLinkId = Object.fromEntries(response.item.map((item) => [item.linkId, item])) as Record<
    string,
    QuestionnaireResponseItem
  >;
  return {
    insuranceId: getQuestionnaireAnswerString(byLinkId[VERIFICATION_SNAPSHOT_LINK_IDS.insuranceId]),
    insuranceCompany: getQuestionnaireAnswerString(
      byLinkId[VERIFICATION_SNAPSHOT_LINK_IDS.insuranceCompany],
    ),
    insuranceGroupNumber: getQuestionnaireAnswerString(
      byLinkId[VERIFICATION_SNAPSHOT_LINK_IDS.insuranceGroupNumber],
    ),
  };
}

/**
 * Reads insurance snapshot from verification response meta tags and legacy items.
 * Falls back to item-based values when meta tags exist but are all empty.
 */
export function getInsuranceVerificationSnapshot(
  response: QuestionnaireResponse | undefined,
): InsuranceVerificationSnapshot | undefined {
  if (!response) return undefined;
  const snapshotFromMetaTags = parseInsuranceSnapshotFromMetaTags(response);
  if (
    snapshotFromMetaTags &&
    (snapshotFromMetaTags.insuranceId !== "" ||
      snapshotFromMetaTags.insuranceCompany !== "" ||
      snapshotFromMetaTags.insuranceGroupNumber !== "")
  )
    return snapshotFromMetaTags;
  const snapshotFromItems = parseInsuranceSnapshotFromItems(response);
  if (!snapshotFromItems) return undefined;
  if (
    snapshotFromItems.insuranceId === "" &&
    snapshotFromItems.insuranceCompany === "" &&
    snapshotFromItems.insuranceGroupNumber === ""
  ) {
    return undefined;
  }
  return snapshotFromItems;
}
